import { Problem, Settings, DailySchedule, Me, SiteConfig } from '../types/problem';

// Same origin in production (the Go server hosts the app); `npm start` proxies /api to :8080.
const API_BASE = process.env.REACT_APP_API_URL ?? '';

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(data?.error || `Request failed (${res.status})`, res.status, data?.code);
  }
  return data as T;
}

// ---------- Account ----------

export const fetchConfig = () => request<SiteConfig>('/api/config');
export const fetchMe = () => request<Me>('/api/me');

export const login = (email: string, password: string) =>
  request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });

export const register = (name: string, email: string, password: string) =>
  request('/api/auth/register', { method: 'POST', body: JSON.stringify({ name, email, password }) });

export const logout = () => request('/api/auth/logout', { method: 'POST' });

// ---------- Problems ----------

export function fetchProblems(): Promise<Problem[]> {
  return request('/api/problems');
}

export function markRevised(slug: string, correct: boolean): Promise<{ success: boolean; nextReview: number }> {
  return request(`/api/problems/${encodeURIComponent(slug)}`, {
    method: 'POST',
    body: JSON.stringify({ correct }),
  });
}

export function importProblems(data: Record<string, unknown>): Promise<{ imported: number; skipped: number }> {
  return request('/api/problems/import', { method: 'POST', body: JSON.stringify(data) });
}

export interface EvaluateResult {
  correct: boolean;
  feedback: string;
}

export function evaluateAnswer(data: {
  problemTitle: string;
  approach: string;
  notes: string;
  userAnswer: string;
}): Promise<EvaluateResult> {
  return request('/api/evaluate', { method: 'POST', body: JSON.stringify(data) });
}

export function fetchSettings(): Promise<Settings> {
  return request('/api/settings');
}

export function updateSettings(settings: Settings): Promise<Settings> {
  return request('/api/settings', { method: 'PUT', body: JSON.stringify(settings) });
}

export function fetchTodaySchedule(): Promise<DailySchedule> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return request(`/api/schedule/today?tz=${encodeURIComponent(tz)}`);
}

// ---------- Payments ----------

export interface SubscribeResult {
  subscriptionId: string;
  keyId: string;
  name: string;
  email: string;
  label: string;
}

export const subscribe = (plan: string) =>
  request<SubscribeResult>('/api/payments/subscribe', { method: 'POST', body: JSON.stringify({ plan }) });

export const verifySubscription = (data: Record<string, string>) =>
  request<Me>('/api/payments/verify', { method: 'POST', body: JSON.stringify(data) });

export const cancelSubscription = () => request<Me>('/api/payments/cancel', { method: 'POST' });
